"use client";

import { useState, useMemo, ChangeEvent } from "react";
import { Token, Pair, PoolRiskApiResponseObject } from "@/utils/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STABLECOIN_IDS } from "@/utils/utilities";
import { TableColumn, TokenTableBody, TokenTableHeader } from "./Tokens/TokenTableParts";
import { TokenLogos } from '@/lib/tokenLogos';

type TokenFilter = "all" | "stablecoins" | "hot";

interface TokenTableProps {
  tokens: Token[];
  pairs: Pair[];
  poolRiskData: PoolRiskApiResponseObject[];
  logos: TokenLogos;
}

export function TokenTable({ tokens, pairs, poolRiskData, logos }: TokenTableProps) {
  const [selectedTab, setSelectedTab] = useState<TokenFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const realTokenRegex = useMemo(() => /^[^:]+:G[A-Z0-9]{55}$/, []);

  const filteredRealTokens = useMemo(() => {
    return tokens.filter(
      (token) => realTokenRegex.test(token.name) || token.symbol.toUpperCase() === "XLM",
    );
  }, [tokens, realTokenRegex]);

  const pairMap = useMemo(() => {
    const m = new Map<string, Pair>();
    for (const p of pairs) m.set(p.id, p);
    return m;
  }, [pairs]);

  const volumeMap = useMemo(() => {
    const volMap = new Map<string, number>();
    for (const pd of poolRiskData) {
      const vol = parseFloat(pd.volume);
      const p = pairMap.get(pd.pairId);
      if (!p) continue;
      volMap.set(p.token0, (volMap.get(p.token0) ?? 0) + vol);
      volMap.set(p.token1, (volMap.get(p.token1) ?? 0) + vol);
    }
    return volMap;
  }, [poolRiskData, pairMap]);

  const tvlMap = useMemo(() => {
    const tvlMapInner = new Map<string, number>();
    for (const p of pairs) {
      const t0 = p.token0;
      const t1 = p.token1;
      if (p.tvl) {
        tvlMapInner.set(t0, (tvlMapInner.get(t0) ?? 0) + p.tvl);
        tvlMapInner.set(t1, (tvlMapInner.get(t1) ?? 0) + p.tvl);
      }
    }
    return tvlMapInner;
  }, [pairs]);

  const tokenPairCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const token of filteredRealTokens) {
      map.set(token.id, token.pairs.length);
    }
    return map;
  }, [filteredRealTokens]);

  const topPairCountTokens = useMemo(() => {
    const arr = Array.from(tokenPairCountMap.entries());
    arr.sort((a, b) => b[1] - a[1]);
    return new Set<string>(arr.slice(0, 10).map((i) => i[0]));
  }, [tokenPairCountMap]);

  const topVolumeTokens = useMemo(() => {
    const arr = Array.from(volumeMap.entries());
    arr.sort((a, b) => b[1] - a[1]);
    return new Set<string>(arr.slice(0, 10).map((i) => i[0]));
  }, [volumeMap]);

  const hotTokens = useMemo(() => {
    const s = new Set<string>();
    for (const tid of Array.from(topPairCountTokens)) s.add(tid);
    for (const tid of Array.from(topVolumeTokens)) s.add(tid);
    return s;
  }, [topPairCountTokens, topVolumeTokens]);

  const filteredTokens = useMemo(() => {
    return filteredRealTokens.filter((token) => {
      const q = searchQuery.toLowerCase();
      const matches = token.name.toLowerCase().includes(q) || token.symbol.toLowerCase().includes(q);
      if (!matches) return false;

      if (selectedTab === "stablecoins") {
        return STABLECOIN_IDS.has(token.id);
      } else if (selectedTab === "hot") {
        return hotTokens.has(token.id);
      }

      return true;
    });
  }, [filteredRealTokens, searchQuery, selectedTab, hotTokens]);

  const totalPages = Math.ceil(filteredTokens.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const displayedTokens = filteredTokens.slice(startIndex, startIndex + rowsPerPage);

  const handleReset = () => {
    setSearchQuery("");
    setSelectedTab("all");
    setCurrentPage(1);
  };

  const HEADERS: TableColumn[] = [
    { label: "Token", align: "left" },
    { label: "Price", align: "right" },
    { label: "TVL", align: "right" },
    { label: "Volume", align: "right" },
    { label: "Counter Assets", align: "right" },
    { label: "Last Updated", align: "right" },
    { label: "Actions", align: "right" },
  ];

  return (
    <div>
      {/* Filter bar */}
      <div className="filter-bar">
        <div className="filter-pills">
          <button
            className={`pill${selectedTab === "all" ? " pill-active" : ""}`}
            onClick={() => { setSelectedTab("all"); setCurrentPage(1); }}
          >
            All Tokens
          </button>
          <button
            className={`pill${selectedTab === "stablecoins" ? " pill-active" : ""}`}
            onClick={() => { setSelectedTab("stablecoins"); setCurrentPage(1); }}
          >
            Stablecoins
          </button>
          <button
            className={`pill${selectedTab === "hot" ? " pill-active" : ""}`}
            onClick={() => { setSelectedTab("hot"); setCurrentPage(1); }}
          >
            Hot Tokens
          </button>
        </div>

        <div className="search-wrap">
          <svg className="search-icon-pos" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="search-input"
            placeholder="Search by token name or symbol"
            value={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Search tokens"
          />
        </div>

        <button className="btn-sm" onClick={handleReset}>RESET</button>
      </div>

      {/* Table */}
      <div className="table-wrap anim-fadeSlideInUp20-300ms-delay-400ms">
        <table className="serverSideTokenTable" role="table">
          <caption className="hidden">
            A table of all the tokens tracked by Hoops Finance available in Stellars Soroban Smart
            Contract system.
          </caption>
          <TokenTableHeader ColLabels={HEADERS} />

          <TokenTableBody
            tokens={displayedTokens}
            pairMap={pairMap}
            tvlMap={tvlMap}
            volumeMap={volumeMap}
            noTokensColSpan={HEADERS.length}
            logos={logos}
          />
        </table>

        {/* Pagination */}
        {filteredTokens.length > rowsPerPage && (
          <div className="table-footer">
            <div className="table-footer-left">
              <div className="table-footer-entries">
                <span>Show</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) => {
                    setRowsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger
                    className="h-auto w-auto px-[8px] py-[3px] text-[10px] rounded-[var(--r)] shadow-none"
                    style={{ background: "var(--surface-hi)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}
                    title="Select number of entries per page"
                  >
                    <SelectValue placeholder="10" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>entries</span>
              </div>
              <span>
                Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, filteredTokens.length)}{" "}
                of {filteredTokens.length} entries
              </span>
            </div>
            <div className="table-footer-right">
              <button
                className="btn-sm"
                onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); }}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1,
                )
                .map((page, idx, arr) => (
                  <span key={page}>
                    {idx > 0 && arr[idx - 1] !== page - 1 && (
                      <span style={{ padding: "0 4px", color: "var(--text-tertiary)" }}>...</span>
                    )}
                    <button
                      className={`page-num${currentPage === page ? " page-num-active" : ""}`}
                      onClick={() => { setCurrentPage(page); }}
                      title={`Go to page ${String(page)}`}
                    >
                      {page}
                    </button>
                  </span>
                ))}
              <button
                className="btn-sm"
                onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); }}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                Next
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
